import type { ContentType, VideoProvider, Visibility } from "../api/types";

/** Human-readable labels for each content type. */
export const TYPE_LABELS: Record<ContentType, string> = {
  BLOG_POST: "Blog",
  PHOTO: "Photo",
  VIDEO: "Video",
  LINK: "Link",
  DOCUMENT: "Document",
  ARTICLE: "Article",
};

/**
 * Short archival prefixes used to build accession numbers, and also shown as a
 * placeholder label when a result has no thumbnail (e.g. external links).
 */
export const TYPE_PREFIX_LABEL: Record<ContentType, string> = {
  BLOG_POST: "BL",
  PHOTO: "PH",
  VIDEO: "VD",
  LINK: "LK",
  DOCUMENT: "DC",
  ARTICLE: "AR",
};

/** Backwards-compatible internal alias. */
const TYPE_PREFIX = TYPE_PREFIX_LABEL;

/** Build a catalog/accession code, e.g. ("PHOTO", 7) → "PH-0007". */
export function accession(type: ContentType, id: number): string {
  return `${TYPE_PREFIX[type]}-${String(id).padStart(4, "0")}`;
}

/**
 * Format an ISO instant as e.g. "18 Nov 2024". Returns "" for null.
 *
 * Rendered in UTC on purpose. Content dates are authored as calendar days
 * ("takenAt": "2026-05-02") and stored as midnight UTC, so formatting them in
 * the viewer's zone would show the day before for anyone west of Greenwich.
 */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

/**
 * Format an ISO instant as just its year, e.g. "2019". Returns "" for null.
 *
 * Photographs are captioned with this rather than {@link formatDate}. Most of
 * the archive is dated to the day only because a file carried EXIF; the rest
 * was placed by eye, and a confident "04 Apr 2018" on a photo whose real date
 * is a guess claims more than is known. The year is what is actually reliable,
 * and it is what the year facet filters on.
 *
 * Read in UTC, for the same reason {@link formatDate} is: a calendar day stored
 * as midnight UTC would roll back to 31 December for viewers west of Greenwich.
 */
export function formatYear(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return String(d.getUTCFullYear());
}

/** Format seconds as m:ss, e.g. 184 → "3:04". */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null) return "";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/** Display names for where a video is hosted. */
export const PROVIDER_LABELS: Record<VideoProvider, string> = {
  FILE: "Hosted file",
  YOUTUBE: "YouTube",
  VIMEO: "Vimeo",
  DAILYMOTION: "Dailymotion",
  FACEBOOK: "Facebook",
  TIKTOK: "TikTok",
  LOOM: "Loom",
  STREAMABLE: "Streamable",
  LINK: "Other site (link)",
};

/** Short labels for who may see an item, as shown on badges and in the console. */
export const VISIBILITY_LABELS: Record<Visibility, string> = {
  PUBLIC: "Public",
  MEMBERS: "Members",
  RESTRICTED: "Shared",
  PRIVATE: "Private",
};

/** The longer form, for choosing a visibility. */
export const VISIBILITY_OPTIONS: { value: Visibility; label: string }[] = [
  { value: "PUBLIC", label: "Public — everyone" },
  { value: "MEMBERS", label: "Members — anyone signed in" },
  { value: "RESTRICTED", label: "Specific members only" },
  { value: "PRIVATE", label: "Private — only me" },
];

/**
 * Resolve a video platform's watch URL into its embeddable player URL. Returns
 * null for hosted files (played inline in a <video> tag), for plain links to
 * other sites, and for URLs the platform pattern does not recognise.
 */
export function toEmbedUrl(url: string, provider: string): string | null {
  const match = (pattern: RegExp) => url.match(pattern)?.[1] ?? null;
  switch (provider) {
    case "YOUTUBE": {
      const id = parseYouTubeId(url);
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }
    case "VIMEO": {
      const id = match(/vimeo\.com\/(?:video\/)?(\d+)/);
      return id ? `https://player.vimeo.com/video/${id}` : null;
    }
    case "DAILYMOTION": {
      const id = match(/(?:dailymotion\.com\/(?:embed\/)?video|dai\.ly)\/([a-zA-Z0-9]+)/);
      return id ? `https://www.dailymotion.com/embed/video/${id}` : null;
    }
    case "TIKTOK": {
      const id = match(/tiktok\.com\/(?:@[^/]+\/video|embed(?:\/v2)?)\/(\d+)/);
      return id ? `https://www.tiktok.com/embed/v2/${id}` : null;
    }
    case "LOOM": {
      const id = match(/loom\.com\/(?:share|embed)\/([a-zA-Z0-9]+)/);
      return id ? `https://www.loom.com/embed/${id}` : null;
    }
    case "STREAMABLE": {
      const id = match(/streamable\.com\/(?:e\/)?([a-zA-Z0-9]+)/);
      return id ? `https://streamable.com/e/${id}` : null;
    }
    case "FACEBOOK":
      // Facebook's player takes the whole public video URL rather than an id.
      return /^https:\/\/(?:www\.|m\.)?(?:facebook\.com|fb\.watch)\//.test(url)
        ? `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=false`
        : null;
    default:
      return null; // FILE plays inline; LINK opens the other site
  }
}

function parseYouTubeId(url: string): string | null {
  const patterns = [
    /[?&]v=([\w-]{11})/,
    /youtu\.be\/([\w-]{11})/,
    /youtube(?:-nocookie)?\.com\/(?:embed|shorts|live)\/([\w-]{11})/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
}

/**
 * Compact relative time for comment timestamps, e.g. "3 days ago", "just now".
 * Falls back to an empty string for null/invalid input.
 */
export function timeAgo(iso: string | null | undefined): string {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const seconds = Math.round((Date.now() - then) / 1000);
  if (seconds < 45) return "just now";

  const units: [number, string][] = [
    [60, "minute"],
    [60 * 60, "hour"],
    [60 * 60 * 24, "day"],
    [60 * 60 * 24 * 7, "week"],
    [60 * 60 * 24 * 30, "month"],
    [60 * 60 * 24 * 365, "year"],
  ];

  let chosen: [number, string] = units[0];
  for (const u of units) {
    if (seconds >= u[0]) chosen = u;
  }
  const value = Math.floor(seconds / chosen[0]);
  return `${value} ${chosen[1]}${value === 1 ? "" : "s"} ago`;
}
